import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import * as repo from '../services/dataRepo';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [settings, setSettings] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await repo.getOrCreateSettings();
      setSettings(data);
      return data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettings(null);
      return null;
    }
  }, []);

  /** Précharge les réglages (parcours enfant + pages) dès l’ouverture — utile sur mobile / réseau lent. */
  useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  const fetchQuestions = useCallback(async () => {
    try {
      const data = await repo.listQuestions();
      setQuestions(data);
      return data;
    } catch (error) {
      console.error('Error fetching questions:', error);
      setQuestions([]);
      return null;
    }
  }, []);

  const createSession = useCallback(async (childName, theme = 'default') => {
    try {
      const data = await repo.createSession(childName, theme);
      setSession(data);
      return data;
    } catch (error) {
      console.error('Error creating session:', error);
      return null;
    }
  }, []);

  const updateSessionNameAudio = useCallback(async (sessionId, audioUrl) => {
    try {
      await repo.setSessionNameAudio(sessionId, audioUrl);
      const next = { ...(session || {}), child_name_audio_url: audioUrl };
      setSession(next);
      return next;
    } catch (error) {
      console.error('Error updating session:', error);
      return null;
    }
  }, [session]);

  const addResponse = useCallback(async (sessionId, response) => {
    try {
      await repo.addResponse(sessionId, response);
      return true;
    } catch (error) {
      console.error('Error adding response:', error);
      return false;
    }
  }, []);

  const completeSession = useCallback(async (sessionId) => {
    try {
      await repo.setSessionCompleted(sessionId, true);
      const next = { ...(session || {}), completed: true };
      setSession(next);
      return next;
    } catch (error) {
      console.error('Error completing session:', error);
      return null;
    }
  }, [session]);

  const uploadAudio = useCallback(async (audioData /* sessionId, audioType */) => {
    // NocodeBackend has no file endpoint on our plan; we keep the raw
    // base64 as a data URL and store it directly in responses.audio_url.
    try {
      return repo.buildAudioDataUrl(audioData);
    } catch (error) {
      console.error('Error packaging audio:', error);
      return null;
    }
  }, []);

  const verifyPassword = useCallback(async ({ password, email }) => {
    try {
      const result = await repo.verifyAdminCredentials({ password, email });
      const ok = Boolean(result?.ok);
      setIsAuthenticated(ok);
      return result;
    } catch (error) {
      console.error('Error verifying password:', error);
      setIsAuthenticated(false);
      return { ok: false, detail: 'Erreur inattendue.' };
    }
  }, []);

  const updateSettings = useCallback(async (newSettings) => {
    try {
      const updated = await repo.updateSettings(newSettings);
      setSettings(updated);
      return updated;
    } catch (error) {
      console.error('Error updating settings:', error);
      return null;
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      return await repo.listSessions();
    } catch (error) {
      console.error('Error fetching sessions:', error);
      return [];
    }
  }, []);

  const deleteSession = useCallback(async (sessionId) => {
    try {
      await repo.deleteSession(sessionId);
      return true;
    } catch (error) {
      console.error('Error deleting session:', error);
      return false;
    }
  }, []);

  const createQuestion = useCallback(async (questionData) => {
    try {
      const q = await repo.createQuestion(questionData);
      await fetchQuestions();
      return q;
    } catch (error) {
      console.error('Error creating question:', error);
      return null;
    }
  }, [fetchQuestions]);

  const updateQuestion = useCallback(async (questionId, questionData) => {
    try {
      const q = await repo.updateQuestion(questionId, questionData);
      await fetchQuestions();
      return q;
    } catch (error) {
      console.error('Error updating question:', error);
      return null;
    }
  }, [fetchQuestions]);

  const deleteQuestion = useCallback(async (questionId) => {
    try {
      await repo.deleteQuestion(questionId);
      await fetchQuestions();
      return true;
    } catch (error) {
      console.error('Error deleting question:', error);
      return false;
    }
  }, [fetchQuestions]);

  const reorderQuestions = useCallback(async (questionIds) => {
    const previous = questions;
    const byId = new Map(questions.map((q) => [q.id, q]));
    const optimistic = questionIds
      .map((id, index) => {
        const q = byId.get(id);
        return q ? { ...q, order: index + 1 } : null;
      })
      .filter(Boolean);
    if (optimistic.length) {
      setQuestions(optimistic);
    }
    try {
      await repo.reorderQuestions(questionIds);
      return true;
    } catch (error) {
      console.error('Error reordering questions:', error);
      setQuestions(previous);
      await fetchQuestions();
      return false;
    }
  }, [questions, fetchQuestions]);

  const value = {
    session,
    setSession,
    settings,
    questions,
    isAuthenticated,
    setIsAuthenticated,
    fetchSettings,
    fetchQuestions,
    createSession,
    updateSessionNameAudio,
    addResponse,
    completeSession,
    uploadAudio,
    verifyPassword,
    updateSettings,
    fetchSessions,
    deleteSession,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    reorderQuestions,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
